import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { mediaQueries } from "../../styles/media.js";
import { EmptyStatePanel, EmptyStateButton, emptyStateAction } from "../../components/CaseFilterReset";
import { trackReferralLinkClicked } from "../../utils/analyticsUtils";

export const PARTNERSHIPS_EMAIL = "partnerships@juno.legal";

// The Referrals tab's empty states. CaseFilterReset's copy is hardcoded to cases, and the two
// no-data states here need to say different things: an agent with a branch code and no leads in
// the window should be sent their referral link; an agent whose leads are hidden because no code
// resolves for them (own_only) should be told why and pointed at partnerships.
function ReferralsEmptyState({ kind, searchInput, statusLabel, referralLink, onClearSearch, onShowAll, onRetry }) {
  switch (kind) {
    case "search":
      return (
        <Box>
          <h2>
            No referrals found for <strong>{searchInput}</strong>
          </h2>
          <EmptyStateButton onClick={onClearSearch}>Clear search</EmptyStateButton>
        </Box>
      );

    case "filter":
      return (
        <Box>
          <h2>
            No referrals marked <strong>{statusLabel}</strong>
            {searchInput ? (
              <>
                {" "}
                for <strong>{searchInput}</strong>
              </>
            ) : (
              " in the last 90 days"
            )}
          </h2>
          <EmptyStateButton onClick={onShowAll}>Show all referrals</EmptyStateButton>
        </Box>
      );

    case "own_only":
      return (
        <Box>
          <h2>We only show referrals you submitted yourself</h2>
          <p>
            To see everything referred under your branch’s code, email{" "}
            <a href={`mailto:${PARTNERSHIPS_EMAIL}`}>{PARTNERSHIPS_EMAIL}</a> and we’ll set it up.
          </p>
        </Box>
      );

    case "failed":
      return (
        <Box>
          <h2>We couldn’t load your referrals</h2>
          <EmptyStateButton onClick={onRetry}>Try again</EmptyStateButton>
        </Box>
      );

    default:
      return (
        <Box>
          <h2>No referrals in the last 90 days</h2>
          {referralLink ? (
            <>
              <p>Refer a client and they’ll appear here as soon as we’re in touch.</p>
              <ActionLink
                href={referralLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackReferralLinkClicked}
              >
                Refer a new client
              </ActionLink>
            </>
          ) : (
            <p>
              To start referring clients, email <a href={`mailto:${PARTNERSHIPS_EMAIL}`}>{PARTNERSHIPS_EMAIL}</a>.
            </p>
          )}
        </Box>
      );
  }
}

export default ReferralsEmptyState;

ReferralsEmptyState.propTypes = {
  kind: PropTypes.oneOf(["none", "own_only", "search", "filter", "failed"]).isRequired,
  searchInput: PropTypes.string,
  statusLabel: PropTypes.string,
  referralLink: PropTypes.string,
  onClearSearch: PropTypes.func,
  onShowAll: PropTypes.func,
  onRetry: PropTypes.func,
};

// CaseFilterReset's panel, centred, with room for a line of explanation under the heading.
const Box = styled(EmptyStatePanel)`
  text-align: center;
  padding: 2.5rem 1.5rem;
  ${mediaQueries.md} {
    padding: 4rem;
  }

  h2 {
    font-size: var(--juno-font-size-h2-sm);
    ${mediaQueries.md} {
      font-size: var(--juno-font-size-h2-md);
    }
  }

  p {
    margin: 0.75rem 0 0;
    max-width: 32rem;
    color: var(--juno-color-text-3);

    a {
      color: var(--juno-color-purple);
      font-weight: var(--juno-font-weight-bold);
    }
  }
`;

const ActionLink = styled.a`
  ${emptyStateAction}
  color: var(--juno-color-white) !important;
  border-bottom: 0 !important;

  &:hover {
    color: var(--juno-color-purple) !important;
  }
`;
